import React, { useState, useEffect } from 'react';
import { ClientVoiceQuote } from '../types';
import { apiService } from '../services/api';

interface ClientVoiceEditorProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  initialVoices: ClientVoiceQuote[];
  onSave: (updatedVoices: ClientVoiceQuote[]) => void;
}

interface EditableVoice extends ClientVoiceQuote {
  isNew?: boolean;
  isDeleted?: boolean;
}

const ClientVoiceEditor: React.FC<ClientVoiceEditorProps> = ({
  isOpen,
  onClose,
  clientId,
  initialVoices,
  onSave
}) => {
  const [voices, setVoices] = useState<EditableVoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setVoices(initialVoices.map(voice => ({ ...voice })));
      setError(null);
    }
  }, [isOpen, initialVoices]);

  const handleVoiceChange = (index: number, field: 'quote' | 'source', value: string) => {
    setVoices(prev => prev.map((voice, i) => 
      i === index ? { ...voice, [field]: value } : voice
    ));
  };

  const addVoice = () => {
    setVoices(prev => [...prev, {
      id: `temp-${Date.now()}`,
      quote: '',
      source: '',
      isNew: true
    }]);
  };

  const removeVoice = (index: number) => {
    setVoices(prev => prev.map((voice, i) => 
      i === index ? { ...voice, isDeleted: true } : voice
    ));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Process deletions
      const toDelete = voices.filter(voice => voice.isDeleted && !voice.isNew);
      for (const voice of toDelete) {
        await apiService.deleteClientVoice(clientId, voice.id);
      }

      // Process updates
      const toUpdate = voices.filter(voice => !voice.isNew && !voice.isDeleted);
      for (const voice of toUpdate) {
        await apiService.updateClientVoice(clientId, voice.id, {
          quote: voice.quote,
          source: voice.source
        });
      }

      // Process new additions
      const toAdd = voices.filter(voice => voice.isNew && !voice.isDeleted && voice.quote.trim() && voice.source.trim());
      const newVoices: ClientVoiceQuote[] = [];
      for (const voice of toAdd) {
        const newVoice = await apiService.createClientVoice(clientId, {
          quote: voice.quote,
          source: voice.source
        });
        newVoices.push(newVoice);
      }

      // Get updated list
      const updatedVoices = await apiService.getClientVoice(clientId);
      onSave(updatedVoices);
      onClose();
    } catch (err) {
      console.error('Failed to save client voices:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const activeVoices = voices.filter(voice => !voice.isDeleted);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0, color: '#1a1a1a' }}>
            Edit Client Voice
          </h2>
          <div>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Satoshi, sans-serif',
                backgroundColor: '#6c757d',
                color: 'white',
                marginRight: '8px',
              }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Satoshi, sans-serif',
                backgroundColor: '#007bff',
                color: 'white',
              }}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Direct quotes from the client — questions, concerns, and recurring comments.
            </p>
            <button
              onClick={addVoice}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Satoshi, sans-serif',
                backgroundColor: '#28a745',
                color: 'white',
              }}
            >
              + Add Quote
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {activeVoices.map((voice, index) => (
              <div
                key={voice.id}
                style={{
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: voice.isNew ? '#f8f9fa' : 'white',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Quote
                  </label>
                  <button
                    onClick={() => removeVoice(index)}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                    title="Remove quote"
                  >
                    ×
                  </button>
                </div>
                <textarea
                  value={voice.quote}
                  onChange={(e) => handleVoiceChange(index, 'quote', e.target.value)}
                  placeholder="Enter the client quote..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '8px 12px',
                    border: '1px solid #e1e5e9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Satoshi, sans-serif',
                    resize: 'vertical',
                    marginBottom: '12px',
                  }}
                />
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Source
                  </label>
                  <input
                    type="text"
                    value={voice.source}
                    onChange={(e) => handleVoiceChange(index, 'source', e.target.value)}
                    placeholder="e.g., (Product team, internal metrics review)"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Satoshi, sans-serif',
                    }}
                  />
                </div>
              </div>
            ))}

            {activeVoices.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: '#6b7280',
                fontSize: '14px'
              }}>
                No client voice quotes yet. Click "Add Quote" to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientVoiceEditor; 